import type { CSSProperties, ImgHTMLAttributes } from 'react';

export type StaticImageData = {
  src: string;
  height: number;
  width: number;
  blurDataURL?: string;
};

export type StaticRequire = {
  default: StaticImageData;
};

export type StaticImport = StaticRequire | StaticImageData;

export type ImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src?: string | StaticImport | null;
  fill?: boolean;
  priority?: boolean;
  objectFit?: CSSProperties['objectFit'];
  objectPosition?: CSSProperties['objectPosition'];
  quality?: number;
  sizes?: string;
  placeholder?: 'blur' | 'empty';
  blurDataURL?: string;
  unoptimized?: boolean;
  loader?: (...args: any[]) => string;
};

const normalizeStaticImport = (src: StaticImport) =>
  'default' in src ? src.default : src;

const getImageSrc = (src: ImageProps['src']) => {
  // 兼容 CMS 图片字段为空或业务组件按条件传 undefined 的历史用法。
  if (!src) return undefined;
  if (typeof src === 'string') return src;
  return normalizeStaticImport(src).src;
};

const getImageSize = (src: ImageProps['src']) => {
  if (!src) return {};
  if (typeof src === 'string') return {};
  const normalized = normalizeStaticImport(src);
  return { width: normalized.width, height: normalized.height };
};

const Image = (props: ImageProps) => {
  const {
    src,
    alt,
    fill,
    style,
    width,
    height,
    loading,
    priority,
    objectFit,
    objectPosition,
    quality,
    sizes,
    placeholder,
    blurDataURL,
    unoptimized,
    loader,
    ...rest
  } = props;

  const baseSize = getImageSize(src);

  const mergedStyle = fill
    ? {
        position: 'absolute' as const,
        inset: 0,
        width: '100%',
        height: '100%',
        ...(objectFit ? { objectFit } : {}),
        ...(objectPosition ? { objectPosition } : {}),
        ...style,
      }
    : {
        ...(objectFit ? { objectFit } : {}),
        ...(objectPosition ? { objectPosition } : {}),
        ...style,
      };

  return (
    <img
      {...rest}
      src={getImageSrc(src)}
      alt={alt ?? ''}
      width={fill ? undefined : width ?? baseSize.width}
      height={fill ? undefined : height ?? baseSize.height}
      style={mergedStyle}
      loading={priority ? 'eager' : loading}
      fetchPriority={priority ? 'high' : rest.fetchPriority}
    />
  );
};

export default Image;
